/** Bounded AXOS commerce attestation validation shared by ingestion and display.
 * Receipt contents remain the authenticated producer's responsibility.
 * This validates current observed created-order cohort state, never historical as-of state.
 * See commerceCoverage.d.ts and docs/axos-commerce-field-coverage.md for the exact contract.
 */
const COMMERCE_CLIENT = "ibd-assist-axos";
const COMMERCE_DEFINITION = "shopify-paid-created-cohort-current-state-v1";
const fail = () => {
  throw new Error("Invalid commerce field coverage");
};
const object = (value) => value && typeof value === "object" && !Array.isArray(value) ? value : fail();
const keys = (value, required, optional = []) => {
  if (required.some((key) => !Object.prototype.hasOwnProperty.call(value, key))
    || Object.keys(value).some((key) => ![...required, ...optional].includes(key))) fail();
};
const isDay = (value) => typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)
  && Number.isFinite(Date.parse(value)) && new Date(value).toISOString().slice(0, 10) === value;
const token = (value) => typeof value === "string" && /^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/.test(value);
const utcTime = (value) => typeof value === "string"
  && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,6})?Z$/.test(value)
  && isDay(value.slice(0, 10)) && Number.isFinite(Date.parse(value))
  && new Date(value).toISOString().slice(0, 19) === value.slice(0, 19);
const nonnegativeInteger = (value) => typeof value === "number" && Number.isSafeInteger(value) && value >= 0 && value <= 999_999_999_999_999;
function validateProducts(raw, proof, row) {
  const facet = object(raw);
  keys(facet, ["version", "status", "identity", "totalUnits", "totalLineItems", "products"]);
  if (facet.version !== 1 || facet.status !== "complete" || facet.identity !== "shopify-product-id"
    || proof.fields.revenueCents?.status !== "complete" || proof.fields.orders?.status !== "complete"
    || !nonnegativeInteger(row.revenueCents) || !nonnegativeInteger(facet.totalUnits) || !nonnegativeInteger(facet.totalLineItems)
    || !Array.isArray(facet.products) || facet.products.length > 20) fail();
  const seen = new Set();
  let revenue = 0, units = 0, orders = 0, lines = 0;
  for (const rawProduct of facet.products) {
    const product = object(rawProduct);
    keys(product, ["productId", "title", "revenueCents", "units", "orders", "lineItems"]);
    if (typeof product.productId !== "string" || !/^[1-9][0-9]{0,19}$/.test(product.productId) || seen.has(product.productId)
      || typeof product.title !== "string" || !product.title.trim() || [...product.title].length > 160
      || /[\u0000-\u001f\u007f]|[\uD800-\uDFFF]/u.test(product.title)
      || ["revenueCents", "units", "orders", "lineItems"].some((key) => !nonnegativeInteger(product[key]))
      || product.orders < 1 || product.orders > row.orders || product.orders > product.lineItems || product.lineItems > product.units) fail();
    seen.add(product.productId);
    revenue += product.revenueCents; units += product.units; orders += product.orders; lines += product.lineItems;
  }
  if (![revenue, units, orders, lines].every(Number.isSafeInteger) || revenue !== row.revenueCents
    || units !== facet.totalUnits || lines !== facet.totalLineItems || orders < row.orders
    || (row.orders === 0 && (facet.products.length || units || lines))
    || new TextEncoder().encode(JSON.stringify(proof)).length > 2048) fail();
}
/** Reject invalid supplied proof; absent metadata preserves legacy behavior. */
function validateCommerceCoverage(value, context) {
  if (value === null || value === undefined) return null;
  const proof = object(value);
  if (JSON.stringify(proof).length > 2048) fail();
  keys(proof, ["version", "clientSlug", "day", "timezone", "currency", "scopeId", "definition", "source", "receiptRef", "receiptSha256", "observedAt", "collection", "reconciliation", "fields"], ["productPerformance"]);
  const { row } = context;
  if (context.clientSlug !== COMMERCE_CLIENT || proof.clientSlug !== context.clientSlug || proof.version !== 1
    || !isDay(proof.day) || proof.day !== row.day || proof.timezone !== context.timezone
    || typeof proof.timezone !== "string" || proof.timezone.length > 64
    || proof.currency !== row.currency || typeof proof.currency !== "string" || !/^[A-Z]{3}$/.test(proof.currency)
    || !token(proof.scopeId) || proof.definition !== COMMERCE_DEFINITION || proof.source !== "shopify"
    || !token(proof.receiptRef) || typeof proof.receiptSha256 !== "string" || !/^[a-f0-9]{64}$/.test(proof.receiptSha256)
    || !utcTime(proof.observedAt) || !utcTime(row.updatedAt) || Date.parse(proof.observedAt) > Date.parse(row.updatedAt)
    || !["verified", "pending"].includes(proof.reconciliation)) fail();
  let observedDay;
  try {
    const parts = new Intl.DateTimeFormat("en-US", { timeZone: proof.timezone, year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(new Date(proof.observedAt));
    const part = (type) => parts.find((item) => item.type === type)?.value;
    observedDay = `${part("year")}-${part("month")}-${part("day")}`;
  } catch {
    return fail();
  }
  const collection = object(proof.collection);
  keys(collection, ["startDay", "endDay", "paginationComplete", "deduplicated", "dayBucketsComplete"]);
  if (!isDay(collection.startDay) || !isDay(collection.endDay)
    || collection.startDay > row.day || collection.endDay < row.day
    || (Date.parse(collection.endDay) - Date.parse(collection.startDay)) / 86_400_000 >= 120
    || ["paginationComplete", "deduplicated", "dayBucketsComplete"].some((key) => typeof collection[key] !== "boolean")) fail();
  const fields = object(proof.fields);
  keys(fields, [], ["revenueCents", "orders"]);
  if (!Object.keys(fields).length) fail();
  for (const [field, raw] of Object.entries(fields)) {
    const fact = object(raw);
    keys(fact, ["status", "value"]);
    if (!["complete", "partial", "unavailable"].includes(fact.status) || fact.value !== row[field]
      || (fact.value !== null && (typeof fact.value !== "number" || !Number.isSafeInteger(fact.value)
        || Math.abs(fact.value) > 999_999_999_999_999 || (field === "orders" && fact.value < 0)))) fail();
    if (fact.status === "complete" && (fact.value === null || !collection.paginationComplete || !collection.deduplicated
      || !collection.dayBucketsComplete || collection.endDay >= observedDay || proof.reconciliation !== "verified")) fail();
  }
  if (Object.prototype.hasOwnProperty.call(proof, "productPerformance")) validateProducts(proof.productPerformance, proof, row);
  return proof;
}
/** Receipt IDs may differ per day, but the cohort and current source observation must match. */
function commerceScopeKey(proof) {
  return JSON.stringify([proof.clientSlug, proof.timezone, proof.currency, proof.scopeId, proof.definition, proof.source, proof.observedAt]);
}
export {
  COMMERCE_CLIENT,
  COMMERCE_DEFINITION,
  commerceScopeKey,
  validateCommerceCoverage
};
